import { Box, Button, Stack, Typography } from '@evaneos/b2b-ui';
import AttachmentsIcon from '@evaneos/b2b-ui/icons/LibraryBooksOutlined';

import { compareAsc, isSameDay, parseISO } from 'date-fns';
import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { ERROR, PENDING, SUCCESS } from '../../../design-system/constants';
import { Loader } from '../../../design-system/Loader';
import { getDraftMessageRequest, markMessageAsReadRequest } from '../../../resources/discussion';
import {
    getDraftMessageStatus as getDraftMessageStatusSelector,
    getPendingOrErrorSendingMessageIdsByDossierId,
    updateMessageStatus,
} from '../../../resources/discussion/selectors';
import {
    DiscussionItem,
    Message as MessageType,
    ScheduledMessage as ScheduledMessageType,
} from '../../../resources/discussion/types';
import { DossierId } from '../../../resources/dossier/types';
import { useAuth } from '../../../shared/providers/AuthProvider';
import { MessageTemplate } from '../../../resources/message-template/types';
import { useGetPhoneCalls } from '../../../resources/phoning/queries';
import { useGetTrackedTripProperties, useTrackEventAmplitude } from '../../../shared/analytics';
import { useAppDispatch, useAppSelector } from '../../../shared/redux';
import { ErrorOverlay } from '../../Pipeline/components/Error';
import { useGetAgentByDossierId, useGetDossierData } from '../../../resources/dossier/queries';
import { getToday } from '../../../shared/utilities/date';
import {
    useFilteredDiscussionGroupedByDate,
    useLastTravellerMessage,
} from '../../../resources/discussion/queries';
import { GlobalLoader } from '../../../shared/components/layout/GlobalLoader';

import { AttachmentsRightPanel } from './components/AttachmentsRightPanel';
import s from './components/Discussion.css';
import { Message } from './components/Message';
import { MessageTemplatesPanel } from './components/MessageTemplatesPanel';
import { ScheduledMessage } from './components/ScheduledMessage';
import { SendingMessage } from './components/SendingMessage';
import { useAttachmentManagement } from './hooks/useAttachmentManagement';
import { useDiscussionAutoScroll } from './hooks/useDiscussionAutoScroll';
import { EditMessageBox } from './components/message-box/EditMessageBox';
import { NewMessageBox } from './components/message-box/NewMessageBox';
import { CallButtonGroup } from './components/CallButtonGroup';
import { PhoneCallCard } from './components/PhoneCallCard';

export function Discussion({ isSidePanelOpen }: { isSidePanelOpen?: boolean }) {
    const { dossierId } = useParams<{ dossierId: DossierId }>();
    const { formatMessage, formatDate } = useIntl();
    const dispatch = useAppDispatch();
    const { trackEvent } = useTrackEventAmplitude();
    const trackedTripProperties = useGetTrackedTripProperties(dossierId);

    const urlParams = new URLSearchParams(location.search);
    const targetPhoneCallId = Number(urlParams.get('phoneCall'));
    const isFromNotification = urlParams.get('fromNotification') === 'true';

    useGetDossierData(dossierId);

    const {
        messages,
        isResponseRequiredState,
        scheduledMessage,
        refetch: refetchDiscussion,
        status: getDiscussionStatus,
    } = useFilteredDiscussionGroupedByDate(dossierId);
    const lastTravellerMessage = useLastTravellerMessage(dossierId);
    const { status: getPhoneCallsStatus, refetch: refetchPhoneCalls } = useGetPhoneCalls(dossierId);
    const getDraftMessageStatus = useAppSelector(getDraftMessageStatusSelector(dossierId));
    const sendingMessageIds = useAppSelector(
        getPendingOrErrorSendingMessageIdsByDossierId(dossierId),
    );
    const { isImpersonated, userId } = useAuth();
    const editingMessageStatus = useAppSelector(updateMessageStatus(dossierId));

    const [displayAttachmentsPanel, setDisplayAttachmentsPanel] = useState(false);

    const {
        allAttachments,
        getAttachments: getAttachmentsForMessage,
        addAttachment,
        removeAttachment,
    } = useAttachmentManagement(sendingMessageIds);

    const assignedAgent = useGetAgentByDossierId(dossierId);

    const isOwner = assignedAgent && assignedAgent.id === userId?.toString();

    const discussionRef = useRef<HTMLDivElement>(null);
    const { scrollToBottom } = useDiscussionAutoScroll(discussionRef, messages, sendingMessageIds);

    const [displayMessageTemplatesPanel, setDisplayMessageTemplatesPanel] = useState(false);
    const [selectedMessageTemplate, setSelectedMessageTemplate] = useState<MessageTemplate | null>(
        null,
    );

    const [editingMessage, setEditingMessage] = useState<
        MessageType | ScheduledMessageType | null | undefined
    >(null);

    const [prevEditingStatus, setPrevEditingStatus] = useState(editingMessageStatus);
    if (prevEditingStatus !== editingMessageStatus) {
        setPrevEditingStatus(editingMessageStatus);
        if (editingMessageStatus === SUCCESS || editingMessageStatus === ERROR) {
            setEditingMessage(null);
        }
    }

    const handleOpenAttachmentsPanelClick = () => {
        setDisplayMessageTemplatesPanel(false);
        setDisplayAttachmentsPanel(!displayAttachmentsPanel);

        trackEvent((ampli, defaultProperties) => {
            ampli.listAttachmentsClicked({
                ...defaultProperties,
                ...trackedTripProperties,
            });
        });
    };

    function renderDate(date: Date): JSX.Element {
        const displayedDate = isSameDay(getToday(), date)
            ? formatMessage({ id: 'global.today' })
            : formatDate(date, {
                  day: '2-digit',
                  month: 'long',
                  year: 'numeric',
              });
        return (
            <Box
                sx={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginTop: { xs: 3, md: 4 },
                    marginBottom: { xs: 1, md: 2 },
                }}
                data-testid="message-group-date"
            >
                <Typography variant="caption" fontWeight="bold" color="textSecondary">
                    {displayedDate}
                </Typography>
            </Box>
        );
    }

    function getSpacingForItem(
        currentItem: DiscussionItem,
        nextItem: DiscussionItem | undefined,
    ): number {
        // If no next message, no margin
        if (!nextItem) return 0;

        const isCurrentPhoneCall = !('authorId' in currentItem);
        const isNextPhoneCall = !('authorId' in nextItem);

        // If one of them is a phone call, reduced spacing (8px)
        if (isCurrentPhoneCall || isNextPhoneCall) return 1;

        // Both are messages - compare type (agent vs traveler)
        const isCurrentFromAgent = currentItem.isFromAgent;
        const isNextFromAgent = nextItem.isFromAgent;

        // Same author type (agent-agent or traveler-traveler) = reduced spacing (8px)
        // Different types (agent-traveler or traveler-agent) = large spacing (24px)
        return isCurrentFromAgent === isNextFromAgent ? 1 : 3;
    }

    function renderMessages(discussionItems: DiscussionItem[]) {
        return discussionItems.map((discussionItem, index) => {
            const nextItem = discussionItems[index + 1];
            const spacing = getSpacingForItem(discussionItem, nextItem);

            if ('authorId' in discussionItem) {
                return (
                    <Box key={discussionItem.id} marginBottom={spacing}>
                        <Message
                            message={discussionItem}
                            isResponseRequiredState={isResponseRequiredState}
                            onEdit={() => {
                                setEditingMessage(discussionItem);
                                setTimeout(() => scrollToBottom(), 0);
                            }}
                            isBlurred={
                                editingMessage != null && editingMessage.id !== discussionItem.id
                            }
                            isEditing={
                                editingMessage != null && editingMessage.id === discussionItem.id
                            }
                        />
                    </Box>
                );
            }

            return (
                <Box key={discussionItem.id} marginBottom={spacing}>
                    <PhoneCallCard
                        discussionPhoneCall={discussionItem}
                        shouldAutoOpen={
                            isFromNotification && targetPhoneCallId === discussionItem.id
                        }
                    />
                </Box>
            );
        });
    }

    function handleMessageTemplatesPanel(): void {
        setDisplayMessageTemplatesPanel(!displayMessageTemplatesPanel);

        setDisplayAttachmentsPanel(false);
    }

    function setSelection(selectedMessageTemplate: MessageTemplate): void {
        setSelectedMessageTemplate(selectedMessageTemplate);
    }

    function removeSelectedMessageTemplate(): void {
        if (selectedMessageTemplate) {
            setSelectedMessageTemplate(null);
        }
    }

    const renderMessageBox = (): React.ReactElement => {
        if (editingMessage) {
            return (
                <EditMessageBox
                    dossierId={dossierId}
                    editingMessage={editingMessage}
                    onCancel={() => setEditingMessage(null)}
                    onHeightChange={scrollToBottom}
                />
            );
        }

        return (
            <NewMessageBox
                onHeightChange={scrollToBottom}
                handleMessageTemplatesPanel={handleMessageTemplatesPanel}
                selectedMessageTemplate={selectedMessageTemplate}
                removeSelectedMessageTemplate={removeSelectedMessageTemplate}
                disableAutoFocus={isFromNotification}
                getAttachments={getAttachmentsForMessage}
                addAttachment={addAttachment}
                removeAttachment={removeAttachment}
            />
        );
    };

    useEffect(() => {
        if (
            getDiscussionStatus === SUCCESS &&
            !isImpersonated &&
            isOwner &&
            lastTravellerMessage?.id
        ) {
            dispatch(
                markMessageAsReadRequest(dossierId, parseInt(lastTravellerMessage.id.toString())),
            );
        }
    }, [dispatch, lastTravellerMessage, getDiscussionStatus, isImpersonated, isOwner, dossierId]);

    if (
        getDiscussionStatus === PENDING ||
        getPhoneCallsStatus === PENDING ||
        getDraftMessageStatus === PENDING
    ) {
        return (
            <div className={s.discussionLoading} data-testid="dossier-loader">
                <Loader className={s.loader} />
                <h2>
                    <GlobalLoader />
                </h2>
            </div>
        );
    }
    if (getDiscussionStatus === ERROR) {
        return (
            <div className={s.discussionContainer}>
                <ErrorOverlay
                    onRefresh={() => {
                        void refetchDiscussion();
                        void refetchPhoneCalls();
                        dispatch(getDraftMessageRequest(dossierId));
                    }}
                />
            </div>
        );
    }

    return (
        <div className={s.rightPanelContainer}>
            <div className={s.discussionContainer}>
                {messages && (
                    <Stack direction="column" marginBottom="auto">
                        <Stack
                            direction="row"
                            padding={3}
                            paddingBottom={1}
                            borderBottom="1px solid"
                            borderColor="divider"
                            justifyContent="flex-end"
                            alignItems="center"
                            gap={1}
                        >
                            <Button
                                data-testid="open-attachments-panel-button"
                                variant="outlined"
                                startIcon={<AttachmentsIcon fontSize="small" />}
                                onClick={handleOpenAttachmentsPanelClick}
                            >
                                {formatMessage({
                                    id: 'discussion.attachments_list.open_button',
                                })}
                            </Button>
                            <CallButtonGroup dossierId={dossierId} />
                        </Stack>
                    </Stack>
                )}
                <Stack
                    ref={discussionRef}
                    className={s.discussion}
                    padding={{ xs: 2, md: 3 }}
                    paddingTop={0}
                    data-testid="discussion"
                >
                    {messages &&
                        Object.keys(messages)
                            .sort((a, b) => compareAsc(parseISO(a), parseISO(b)))
                            .map((date) => (
                                <React.Fragment key={date}>
                                    {renderDate(messages[date][0].sendDate)}
                                    <Stack>{renderMessages(messages[date])}</Stack>
                                </React.Fragment>
                            ))}
                    {sendingMessageIds.map((sendingMessageId) => (
                        <SendingMessage
                            key={sendingMessageId}
                            sendingMessageId={sendingMessageId}
                            attachments={allAttachments[sendingMessageId] || {}}
                        />
                    ))}
                    {scheduledMessage && (
                        <ScheduledMessage
                            data-testid="scheduled-messages-preview"
                            key={scheduledMessage.id}
                            scheduledMessage={scheduledMessage}
                            onEdit={() => {
                                setEditingMessage(scheduledMessage);
                                setTimeout(() => scrollToBottom(), 0);
                            }}
                            isBlurred={
                                editingMessage != null && editingMessage.id !== scheduledMessage.id
                            }
                            isEditing={
                                editingMessage != null && editingMessage.id === scheduledMessage.id
                            }
                        />
                    )}
                </Stack>

                {renderMessageBox()}

                <MessageTemplatesPanel
                    dossierId={dossierId}
                    handleMessageTemplatesPanel={handleMessageTemplatesPanel}
                    displayPanel={displayMessageTemplatesPanel}
                    applyMessageTemplate={setSelection}
                />
            </div>

            <AttachmentsRightPanel
                dossierId={dossierId}
                isSidePanelOpen={isSidePanelOpen}
                isDisplayed={displayAttachmentsPanel}
                onClose={() => setDisplayAttachmentsPanel(false)}
            />
        </div>
    );
}
